import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import styled, { keyframes } from 'styled-components';
// import ArchetypeStockShelf from './ImmersiveStoreComponents/ArchetypeStockShelf';

// Set to false to restore the previous 12-card grid instantly
const USE_SELECTOR_LAYOUT = true;

const ARCHETYPES_API_URL =
  'https://us-central1-archetype-builder-api.cloudfunctions.net/api/archetypes/';

const MAX_IMAGE_HEIGHT = 120;

const BODIES = {
  creator: [
    ['   🎨✺✧ ', ' ( ⟡‿⟡ ) ', ' /|░▒▓| ', ' /═══\\✧ '],
    ['   🎨✺✧ ', ' ( ⟡_⟡ ) ', ' /|░▒▓| ', ' /═══\\~✧'],
    ['   🎨✺✧ ', ' ( •_• ) ', ' /|░▒▓| ', ' /═══\\✧ ']
  ],
  magician: [
    ['   🜂✧   ', '  /⟁\\  ', ' (⚝_⚝) ', ' <[✺]>  '],
    ['   🜂✧   ', '  /⟁\\  ', ' (⚝‿⚝) ', ' <[✺]>~ '],
    ['   🜂✧   ', '  /⟁\\  ', ' (•_•) ', ' <[✺]>  ']
  ],
  sage: [
    ['   🜁✧   ', ' (◈_◈) ', ' /|━━━| ', '  | |🜁  '],
    ['   🜁✧   ', ' (◈‿◈) ', ' /|━━━| ', ' ✨| |✨  '],
    ['   🜁✧   ', ' ( -_- ) ', ' /|━━━| ', '  | |🜁  ']
  ],
  explorer: [
    ['  ≋⌖≋  ', ' (•̀_•́) ', ' /|🧭| \\ ', ' / 🌲↝ '],
    ['  ≋⌖≋  ', ' (•̀•́) ', ' /|🧭| \\ ', ' /~🌲  '],
    ['  ≋⌖≋  ', ' (o_o) ', ' /|🧭| \\ ', ' / 🌲  ']
  ],
  rebel: [
    ['   🔥🔥  ', ' (¬‿¬)≡ ', '  \\|⚔️|/', '  / >✦  '],
    ['   🔥🔥  ', ' (¬_¬)≡ ', '  \\|⚔️|/', '  /~>✦  '],
    ['   🔥🔥  ', ' (•_•)≡ ', '  \\|⚔️|/', '  / >✦  ']
  ],
  caregiver: [
    ['   🛡️✧   ', ' ( ^‿^ ) ', '(づ◉‿◉)づ', '  / \\✧  '],
    ['   🛡️✧   ', ' ( ^‿^ ) ', '(づ◉‿◉)づ', '  /~\\✧  '],
    ['   🛡️✧   ', ' ( -_- ) ', '(づ◉‿◉)づ', '  / \\✧  ']
  ],
  hero: [
    ['  ⚔︎✺⚔︎  ', ' (•‿•) ', '╱[▮▮▮]╲', ' /| |\\ '],
    ['  ⚔︎✺⚔︎  ', ' (•_•) ', '╱[▮▮▮]╲', ' /|~|\\ '],
    ['  ⚔︎✺⚔︎  ', ' (>_<) ', '╱[▮▮▮]╲', ' /| |\\ ']
  ],
  ruler: [
    ['   👑✦✦ ', ' ( •_• ) ', '/|█▮█|\\', ' |___|✧ '],
    ['   👑✦✦ ', ' ( •‿• ) ', '/|█▮█|\\', ' |___|~✧'],
    ['   👑✦✦ ', ' ( ¬_¬ ) ', '/|█▮█|\\', ' |___|✧ ']
  ],
  innocent: [
    ['  ✧🌼✧  ', ' (◕‿◕) ', '  /|❀|\\ ', '  / \\✦  '],
    ['  🌼✧🌼 ', ' (◕ω◕) ', '  /|❀|\\ ', ' ✨/ \\✨ '],
    ['  ✧🌼✧  ', ' (◕‿◕) ', '  /|❀|\\ ', '  /~\\✦  ']
  ],
  joker: [
    ['  ♠️♦️♣️ ', ' ( ^o^ ) ', '  \\/|\\// ', '  / \\✦  '],
    ['  ♠️♦️♣️ ', ' ( ^O^ ) ', '  /\\|/\\ ', ' ~\\ /~✦ '],
    ['  ♠️♦️♣️ ', ' ( o_O ) ', '  \\/|\\// ', '  / \\✦  ']
  ],
  everyman: [
    ['   ⚡✧   ', ' ( •_• ) ', '  /|▓|\\ ', '  / \\✧  '],
    ['   ⚡✧   ', ' ( •‿• ) ', '  /|▓|\\ ', '  /~\\✧  '],
    ['   ⚡✧   ', ' ( -_- ) ', '  /|▓|\\ ', '  / \\✧  ']
  ],
  lover: [
    ['  ❤️‍🔥✧ ', ' (♥‿♥) ', '  \\|/  ', '  ♡♡✧  '],
    ['  ❤️‍🔥✧ ', ' (♥ω♥) ', '  /|\\  ', '  ♡~♡✧ '],
    ['  ❤️‍🔥✧ ', ' (˘‿˘) ', '  \\|/  ', '  ♡♡✧  ']
  ]
};

const RARITY = {
  creator:    { tier: 'legendary', loreName: 'Vision Maker',    chance: '1%' },
  magician:   { tier: 'epic',      loreName: 'Reality Bender',  chance: '4%' },
  sage:       { tier: 'epic',      loreName: 'Truth Seeker',    chance: '4%' },
  explorer:   { tier: 'rare',      loreName: 'Wanderer',        chance: '10%' },
  rebel:      { tier: 'rare',      loreName: 'Disruptor',       chance: '10%' },
  caregiver:  { tier: 'uncommon',  loreName: 'Nurturer',        chance: '25%' },
  hero:       { tier: 'uncommon',  loreName: 'Champion',        chance: '25%' },
  ruler:      { tier: 'rare',      loreName: 'Sovereign',       chance: '10%' },
  innocent:   { tier: 'common',    loreName: 'Pure Light',      chance: '60%' },
  joker:      { tier: 'uncommon',  loreName: 'Chaos Spark',     chance: '25%' },
  everyman:   { tier: 'common',    loreName: 'The Relatable',   chance: '60%' },
  lover:      { tier: 'common',    loreName: 'Passionate One',  chance: '60%' },
};

const TIER_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

const STAT_COLORS = {
  DEBUGGING: '#58a6ff',
  PATIENCE: '#3fb950',
  CHAOS: '#f47067',
  WISDOM: '#bc8cff',
  SNARK: '#f0c040'
};

const IMAGE_MAP = {
  creator: 'Creator.png',
  magician: 'Magician.png',
  sage: 'Sage.png',
  explorer: 'Explorer.png',
  rebel: 'Rebel.png',
  caregiver: 'Caregiver.png',
  hero: 'Hero.png',
  ruler: 'Ruler.png',
  innocent: 'Innocent.png',
  joker: 'Joker.png',
  everyman: 'Everyman.png',
  lover: 'Lover.png'
};

const seedStats = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  const s = () => { h = (h * 16807 + 1) & 0x7fffffff; return 25 + (h % 76); };
  return {
    DEBUGGING: s(), PATIENCE: s(), CHAOS: s(), WISDOM: s(), SNARK: s()
  };
};

const capitalize = (name) => name.charAt(0).toUpperCase() + name.slice(1);

const tierColor = (tier) => {
  if (tier === 'uncommon') return '#3fb950';
  if (tier === 'rare') return '#58a6ff';
  if (tier === 'epic') return '#bc8cff';
  if (tier === 'legendary') return '#f0c040';
  return '#8b949e';
};

const highlightJson = (value) => {
  const json = JSON.stringify(value, null, 2);
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let color = '#79c0ff';
      if (/^"/.test(match)) {
        color = /:$/.test(match) ? '#45fe47' : '#a5d6ff';
      } else if (/true|false/.test(match)) {
        color = '#ffa657';
      } else if (/null/.test(match)) {
        color = '#8b949e';
      }
      return `<span style="color:${color}">${match}</span>`;
    }
  );
};

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(220%); }
`;

const AcademyShell = styled.section.attrs({ id: 'academy' })`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
  color: #e6edf3;
`;

const AcademyHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

// const StockWrap = styled.div`
//   margin: 0 0 2.5rem;
//   width: 100%;
// `;

const AcademyTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-family: 'VT323', monospace;
  font-size: clamp(2rem, 5vw, 2.6rem);
  font-weight: 400;
  background: linear-gradient(90deg, #45fe47, #00ff9d, #39ff14);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const AcademySubtitle = styled.p`
  margin: 0;
  color: #8b949e;
  font-size: 1.05rem;
`;

const Stage = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr);
  gap: 1.25rem;
  align-items: stretch;
  margin-bottom: 1.75rem;
  padding: 1.15rem;
  border: 1px solid ${(p) => p.$accent || 'rgba(69, 254, 71, 0.45)'};
  border-radius: 12px;
  background: rgba(10, 10, 10, 0.72);
  box-shadow: 0 0 18px rgba(69, 254, 71, 0.08);

  @media (max-width: 799px) {
    grid-template-columns: 1fr;
  }
`;

const StageMedia = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 280px;
  padding: 1rem;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.35);
  text-align: center;
`;

const StageImage = styled.img`
  max-height: 130px;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  image-rendering: crisp-edges;
  filter: drop-shadow(0 0 18px rgba(69, 254, 71, 0.25));

  @media (max-width: 799px) {
    max-height: 130px;
  }
`;

const StageName = styled.h3`
  margin: 0;
  font-family: 'VT323', monospace;
  font-size: clamp(1.6rem, 3.5vw, 2.1rem);
  font-weight: 400;
  color: #e8ffe9;
  line-height: 1.1;
`;

const StageMotto = styled.p`
  margin: 0;
  max-width: 260px;
  font-family: 'VT323', monospace;
  font-size: 1.15rem;
  color: ${(p) => p.$color || '#8b949e'};
  line-height: 1.3;
`;

const NftBadge = styled.span`
  font-family: 'VT323', monospace;
  font-size: 0.95rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.$color || '#8b949e'};
  opacity: 0.9;
`;

const JsonPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  max-height: 420px;
  overflow: hidden;
  border: 1px solid ${(p) => p.$accent || 'rgba(69, 254, 71, 0.45)'};
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(69, 254, 71, 0.12);
    animation: ${scanline} 5.5s linear infinite;
    pointer-events: none;
  }
`;

const JsonToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid rgba(69, 254, 71, 0.25);
  background: rgba(69, 254, 71, 0.06);
`;

const JsonPath = styled.code`
  font-family: 'VT323', monospace;
  font-size: 1rem;
  color: #45fe47;
  word-break: break-all;
`;

const JsonButton = styled.button.attrs({ type: 'button' })`
  font-family: 'VT323', monospace;
  font-size: 0.95rem;
  padding: 0.3rem 0.65rem;
  color: #45fe47;
  background: transparent;
  border: 1px solid rgba(69, 254, 71, 0.55);
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: rgba(69, 254, 71, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const JsonPre = styled.pre`
  flex: 1;
  margin: 0;
  padding: 0.9rem 1rem 1.2rem;
  overflow: auto;
  font-family: 'Cascadia Code', 'Fira Code', 'VT323', monospace;
  font-size: 0.92rem;
  line-height: 1.35;
  color: #9dff9f;
  white-space: pre-wrap;
  word-break: break-word;

  &::-webkit-scrollbar { width: 6px; height: 6px; }
  &::-webkit-scrollbar-thumb { background: #45fe47; border-radius: 8px; }
`;

const JsonStatus = styled.p`
  margin: 0;
  padding: 1.5rem 1rem;
  font-family: 'VT323', monospace;
  font-size: 1.15rem;
  color: #8b949e;
`;

const ThumbStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 799px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (max-width: 499px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ThumbButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 6px;
  border: 1px solid ${(p) => (p.$active ? '#45fe47' : 'rgba(69, 254, 71, 0.35)')};
  border-radius: 10px;
  background: ${(p) => (p.$active ? 'rgba(69, 254, 71, 0.12)' : 'rgba(10, 10, 10, 0.65)')};
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.15s ease;

  &:hover {
    border-color: #45fe47;
    transform: translateY(-2px);
  }
`;

const ThumbImage = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  image-rendering: crisp-edges;
`;

const ThumbLabel = styled.span`
  font-family: 'VT323', monospace;
  font-size: 0.95rem;
  color: ${(p) => (p.$active ? '#45fe47' : '#c9d1d9')};
`;

const FoundryToggle = styled.button.attrs({ type: 'button' })`
  display: block;
  margin: 1.75rem auto 0;
  padding: 0.45rem 0.9rem;
  font-family: 'VT323', monospace;
  font-size: 1.05rem;
  color: #8b949e;
  background: transparent;
  border: 1px solid #30363d;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: #45fe47;
    border-color: #45fe47;
  }
`;

const ArchetypeCard = memo(({ name, frameIndex, isShiny, showStats, onCopy, viewMode }) => {
  const rarity = RARITY[name] || { tier: 'common', loreName: 'Unknown' };
  const stats = seedStats(name);

  return (
    <div style={{
      background: '#161b22',
      border: '1px solid #30363d',
      borderRadius: '12px',
      padding: '1.2rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <span style={{
        position: 'absolute', top: '12px', right: '12px',
        fontSize: '0.75rem', fontWeight: '600', padding: '2px 10px',
        borderRadius: '9999px', textTransform: 'uppercase',
        background: rarity.tier === 'common' ? '#21262d' : rarity.tier === 'uncommon' ? '#0d2818' :
                    rarity.tier === 'rare' ? '#0d1d30' : rarity.tier === 'epic' ? '#1a0d30' : '#2a1d08',
        color: tierColor(rarity.tier)
      }}>
        {rarity.tier}
      </span>

      <div style={{ fontWeight: '600', fontSize: '1.15rem', marginBottom: '0.1rem', color: '#c9d1d9' }}>
        {name}
      </div>
      <div style={{
        fontSize: '0.9rem', fontStyle: 'italic', marginBottom: '1rem',
        color: tierColor(rarity.tier)
      }}>
        "{rarity.loreName}"
      </div>

      <div style={{
        background: '#0d1117',
        borderRadius: '10px',
        padding: '1rem 0.6rem',
        marginBottom: '1rem',
        minHeight: '160px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {viewMode === 'ascii' ? (
          <pre style={{
            fontFamily: "'Cascadia Code', 'Fira Code', monospace",
            fontSize: '15.5px',
            lineHeight: '1.28',
            color: '#f0f6fc',
            whiteSpace: 'pre',
            margin: 0,
            animation: isShiny ? 'shinyText 1.8s ease-in-out infinite' : 'none'
          }}>
            {BODIES[name][frameIndex].join('\n')}
          </pre>
        ) : (
          <img
            src={`/images/${IMAGE_MAP[name]}`}
            alt={name}
            style={{
              maxHeight: `${MAX_IMAGE_HEIGHT}px`,
              maxWidth: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              imageRendering: 'crisp-edges',
              filter: isShiny ? 'drop-shadow(0 0 15px #45fe47)' : 'none'
            }}
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/280x${MAX_IMAGE_HEIGHT}/161b22/45fe47?text=${name}`;
            }}
          />
        )}
      </div>

      <button
        onClick={() => onCopy(name, viewMode)}
        style={{
          background: '#21262d',
          color: '#45fe47',
          border: '1px solid #30363d',
          borderRadius: '6px',
          padding: '0.65rem 1.2rem',
          fontSize: '0.95rem',
          cursor: 'pointer',
          width: '100%',
          marginBottom: '0.8rem'
        }}
      >
        {viewMode === 'ascii' ? '📋 Copy Current Frame' : '🧬 Clone'}
      </button>

      {showStats && (
        <div style={{ textAlign: 'left', marginTop: '0.5rem' }}>
          {Object.entries(stats).map(([key, value]) => (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              marginBottom: '6px', fontSize: '0.78rem'
            }}>
              <span style={{ width: '72px', textAlign: 'right', fontFamily: 'monospace', color: '#8b949e' }}>
                {key}
              </span>
              <div style={{ flex: 1, height: '7px', background: '#21262d', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${value}%`, background: STAT_COLORS[key], borderRadius: '4px' }} />
              </div>
              <span style={{ width: '28px', textAlign: 'right', fontFamily: 'monospace' }}>{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const FoundryGrid = ({
  filteredList,
  frameIndices,
  isShiny,
  showStats,
  viewMode,
  currentFilter,
  setCurrentFilter,
  setIsShiny,
  setShowStats,
  setViewMode,
  onCopy,
  onBack
}) => (
  <div style={{
    background: 'transparent',
    color: '#e6edf3',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    padding: '2rem 1rem'
  }}>
    {onBack && (
      <FoundryToggle onClick={onBack} style={{ margin: '0 auto 1.5rem' }}>
        ← Back to Academy
      </FoundryToggle>
    )}

    <h1 style={{
      fontFamily: "'VT323', monospace",
      textAlign: 'center',
      fontSize: '2.4rem',
      marginBottom: '0.3rem',
      background: 'linear-gradient(90deg, #45fe47, #00ff9d, #39ff14)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    }}>
      {viewMode === 'ascii' ? 'ARCHETYPE SPRITE FOUNDRY' : 'THE ARCHETYPE ACADEMY'}
    </h1>

    <p style={{
      textAlign: 'center',
      color: '#8b949e',
      marginBottom: '2.5rem',
      fontSize: '1.05rem'
    }}>
      {viewMode === 'ascii'
        ? '12 Jungian Archetypes • Live Animated ASCII Sprites'
        : '12 Jungian Archetypes • Official Academy Illustrations'}
    </p>

    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2.5rem' }}>
      <button
        onClick={() => setViewMode(viewMode === 'ascii' ? 'images' : 'ascii')}
        style={{
          padding: '0.75rem 2rem',
          borderRadius: '9999px',
          border: '2px solid #45fe47',
          background: viewMode === 'ascii' ? '#45fe47' : 'transparent',
          color: viewMode === 'ascii' ? '#0d1117' : '#45fe47',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '1.1rem'
        }}
      >
        {viewMode === 'ascii' ? '🖼️ Switch to Academy Images' : '⚡ Switch to ASCII Sprites'}
      </button>
    </div>

    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '2.5rem',
      flexWrap: 'wrap'
    }}>
      <button onClick={() => setIsShiny(!isShiny)} style={{
        padding: '0.6rem 1.4rem', borderRadius: '8px', border: '1px solid #30363d',
        background: isShiny ? 'linear-gradient(135deg, #f0c040, #f06090)' : '#21262d',
        color: isShiny ? '#0d1117' : '#e6edf3', cursor: 'pointer'
      }}>
        {isShiny ? '✨ Disable Energized' : '✨ Enable Energized'}
      </button>

      <button onClick={() => setShowStats(!showStats)} style={{
        padding: '0.6rem 1.4rem', borderRadius: '8px', border: '1px solid #30363d',
        background: showStats ? '#45fe47' : '#21262d',
        color: showStats ? '#0d1117' : '#e6edf3', cursor: 'pointer'
      }}>
        {showStats ? 'Hide Stats' : 'Show Stats'}
      </button>
    </div>

    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '0.6rem',
      marginBottom: '2.5rem',
      flexWrap: 'wrap'
    }}>
      {['all', ...TIER_ORDER].map((filter) => (
        <button
          key={filter}
          onClick={() => setCurrentFilter(filter)}
          style={{
            padding: '0.5rem 1.1rem',
            borderRadius: '9999px',
            border: '1px solid #30363d',
            background: currentFilter === filter ? '#45fe47' : '#21262d',
            color: currentFilter === filter ? '#0d1117' : '#8b949e',
            cursor: 'pointer',
            fontSize: '0.92rem'
          }}
        >
          {filter === 'all' ? 'All Archetypes' : filter.charAt(0).toUpperCase() + filter.slice(1)}
        </button>
      ))}
    </div>

    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1.6rem',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {filteredList.map((name) => (
        <ArchetypeCard
          key={name}
          name={name}
          frameIndex={frameIndices[name]}
          isShiny={isShiny}
          showStats={showStats}
          onCopy={onCopy}
          viewMode={viewMode}
        />
      ))}
    </div>

    <style>{`
      @keyframes shinyText {
        0%, 100% { color: #f0f6fc; }
        25% { color: #f0c040; }
        50% { color: #60a0f0; }
        75% { color: #f06090; }
      }
    `}</style>
  </div>
);

const JungianArchetypeSprites = () => {
  const [isShiny, setIsShiny] = useState(true);
  const [showStats, setShowStats] = useState(true);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [viewMode, setViewMode] = useState('images');
  const [selectedName, setSelectedName] = useState('creator');
  const [foundryOpen, setFoundryOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [apiByName, setApiByName] = useState({});
  const [apiStatus, setApiStatus] = useState('loading'); // loading | ready | error
  const [apiError, setApiError] = useState(null);

  const [frameIndices, setFrameIndices] = useState(() =>
    Object.keys(BODIES).reduce((acc, key) => { acc[key] = 0; return acc; }, {})
  );

  const loadArchetypes = useCallback(async () => {
    setApiStatus('loading');
    setApiError(null);
    try {
      const response = await fetch(ARCHETYPES_API_URL);
      if (!response.ok) {
        throw new Error(`API ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Unexpected API payload (expected an array)');
      }
      const byName = data.reduce((acc, archetype) => {
        const key = String(archetype?.name || '').trim().toLowerCase();
        if (key) acc[key] = archetype;
        return acc;
      }, {});
      setApiByName(byName);
      setApiStatus('ready');
    } catch (err) {
      setApiByName({});
      setApiStatus('error');
      setApiError(err?.message || 'Failed to load archetypes');
    }
  }, []);

  const reloadApi = loadArchetypes;

  useEffect(() => {
    loadArchetypes();
  }, [loadArchetypes]);

  useEffect(() => {
    let rafId;
    let lastUpdate = Date.now();

    const animate = () => {
      const now = Date.now();
      if (now - lastUpdate >= 650) {
        setFrameIndices((prev) => {
          const updated = { ...prev };
          Object.keys(BODIES).forEach((name) => {
            updated[name] = (updated[name] + 1) % BODIES[name].length;
          });
          return updated;
        });
        lastUpdate = now;
      }
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const speciesList = useMemo(() => Object.keys(BODIES), []);
  const filteredList = useMemo(() =>
    currentFilter === 'all'
      ? speciesList
      : speciesList.filter((name) => RARITY[name]?.tier === currentFilter),
    [speciesList, currentFilter]
  );

  const selectedJson = apiByName[selectedName] || null;

  const copyToClipboard = useCallback(async (name, mode) => {
    if (mode === 'ascii') {
      const frame = BODIES[name][frameIndices[name]];
      await navigator.clipboard.writeText(frame.join('\n'));
      alert(`✅ Copied "${name}" ASCII frame!`);
    } else {
      await navigator.clipboard.writeText(name);
      alert(`✅ Copied "${name}"!`);
    }
  }, [frameIndices]);

  const copyJson = useCallback(async () => {
    if (!selectedJson) return;
    await navigator.clipboard.writeText(JSON.stringify(selectedJson, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }, [selectedJson]);

  const selected = RARITY[selectedName] || { tier: 'common', loreName: 'Unknown', chance: '—' };
  const accent = selectedJson?.color || '#45fe47';
  const showFoundry = !USE_SELECTOR_LAYOUT || foundryOpen;

  if (showFoundry) {
    return (
      <FoundryGrid
        filteredList={filteredList}
        frameIndices={frameIndices}
        isShiny={isShiny}
        showStats={showStats}
        viewMode={viewMode}
        currentFilter={currentFilter}
        setCurrentFilter={setCurrentFilter}
        setIsShiny={setIsShiny}
        setShowStats={setShowStats}
        setViewMode={setViewMode}
        onCopy={copyToClipboard}
        onBack={USE_SELECTOR_LAYOUT ? () => setFoundryOpen(false) : null}
      />
    );
  }

  return (
    <AcademyShell>
      <AcademyHeader>
        <AcademyTitle>THE ARCHETYPE ACADEMY</AcademyTitle>
        <AcademySubtitle>
          Hand-drawn sprites · live Archetype API JSON
        </AcademySubtitle>
      </AcademyHeader>

      {/* <StockWrap>
        <ArchetypeStockShelf />
      </StockWrap> */}

      <Stage $accent={accent}>
        <StageMedia>
          <NftBadge $color={tierColor(selected.tier)}>
            {selected.tier} · {selected.chance}
          </NftBadge>
          <StageImage
            src={`/images/${IMAGE_MAP[selectedName]}`}
            alt={capitalize(selectedName)}
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/320x300/161b22/45fe47?text=${selectedName}`;
            }}
          />
          <StageName>{selectedJson?.name || capitalize(selectedName)}</StageName>
          <StageMotto $color={accent}>
            {selectedJson?.motto
              ? `"${selectedJson.motto}"`
              : `"${selected.loreName}"`}
          </StageMotto>
        </StageMedia>

        <JsonPanel $accent={accent}>
          <JsonToolbar>
            <JsonPath>{`GET /api/archetypes/ → ${selectedName}`}</JsonPath>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {apiStatus === 'error' && (
                <JsonButton onClick={reloadApi}>Retry</JsonButton>
              )}
              <JsonButton onClick={copyJson} disabled={!selectedJson}>
                {copied ? 'Copied' : 'Copy JSON'}
              </JsonButton>
            </div>
          </JsonToolbar>
          {apiStatus === 'loading' && (
            <JsonStatus>Loading live archetype profile…</JsonStatus>
          )}
          {apiStatus === 'error' && (
            <JsonStatus>
              Could not load Archetype API ({apiError}). Check{' '}
              <a
                href={ARCHETYPES_API_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#45fe47' }}
              >
                /api/archetypes/
              </a>
              .
            </JsonStatus>
          )}
          {apiStatus === 'ready' && selectedJson && (
            <JsonPre dangerouslySetInnerHTML={{ __html: highlightJson(selectedJson) }} />
          )}
          {apiStatus === 'ready' && !selectedJson && (
            <JsonStatus>
              No live API profile matched “{capitalize(selectedName)}”.
            </JsonStatus>
          )}
        </JsonPanel>
      </Stage>

      <ThumbStrip>
        {speciesList.map((name) => {
          const active = name === selectedName;
          return (
            <ThumbButton
              key={name}
              $active={active}
              onClick={() => setSelectedName(name)}
              aria-pressed={active}
              aria-label={`Select ${capitalize(name)}`}
            >
              <ThumbImage
                src={`/images/${IMAGE_MAP[name]}`}
                alt=""
                loading="lazy"
              />
              <ThumbLabel $active={active}>{capitalize(name)}</ThumbLabel>
            </ThumbButton>
          );
        })}
      </ThumbStrip>

      <FoundryToggle onClick={() => setFoundryOpen(true)}>
        Open Sprite Foundry
      </FoundryToggle>
    </AcademyShell>
  );
};

export default JungianArchetypeSprites;
